// ------------------- ModificacionesPedidos.tsx -------------------

import React, { useEffect, useState } from 'react';
import Table from './components/Table/Table';
import {
  getAllModificationsByLocalIdAndRangeDate,
} from '../../../../crud/pedidos_api/pedidos_modifications';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import { PedidoModification} from './Types';
import { toast } from 'react-toastify';
import { exportFilteredModifications } from './utils/modificationsExcelExport';
import ModificationStats from './components/Stats/Stats';
import Filter from './components/OffCanvas/OffCanvas';
import FilterBar from './components/FilterBar/FilterBar';
import { getUsersOptions, getMesasOptions } from './utils/utils';
import { LocalSession } from '../../../../types/LocalSession';
import { MPPageTitle } from '../../../../components/ui';



interface HistorialMovimientosProps {
  lastSession?: LocalSession | null;
}

const ModificacionesPedidos: React.FC<HistorialMovimientosProps> = ({ lastSession }) => {
  const [soloSesionActual, setSoloSesionActual] = useState(false);
  // (Paridad Receipts) Ya no sincronizamos fechas al activar "solo sesión actual"; se filtra client-side por sesión
  const [currentView, setCurrentView] = useState<'table' | 'stats'>('table');
  const [filteredModifications, setFilteredModifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  
  // Mobile detection and filter options
  const [isMobile, setIsMobile] = useState(false);
  const [userOptions, setUserOptions] = useState<{ value: number; label: string }[]>([]);
  const [mesaOptions, setMesaOptions] = useState<{ value: number; label: string }[]>([]);

  // Date filter state
  const now = new Date();
  const addDays = (date: Date, days: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };
  // Por defecto, mostrar los últimos 6 meses
  const span = new Date(now);
  span.setMonth(now.getMonth() - 3);

  const [startDate, setStartDate] = useState<string>(span.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(addDays(now, 0));

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [users, mesas] = await Promise.all([
          getUsersOptions(),
          getMesasOptions()
        ]);
        
        setUserOptions(users.map(user => ({ value: user.id, label: user.name })));
        setMesaOptions(mesas.map(mesa => ({ 
          value: mesa.id, 
          label: mesa.tag && mesa.tag.trim() ? mesa.tag : mesa.num 
        })));
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    };
    
    loadFilterOptions();
  }, []);

  // Fetch modifications by date range; si soloSesionActual está activo filtramos por session_id/local_session_id
  useEffect(() => {
    setLoading(true);
    const userContext = localStorage.getItem('user');
    const localId = userContext != null ? JSON.parse(userContext).local_id : 0;

    // Fetch base modificaciones por rango de fechas
    getAllModificationsByLocalIdAndRangeDate(localId, startDate, endDate)
      .then((modsRes) => {
        let modifications = modsRes.data;
        // Nueva lógica de sesión: filtrar por session_id/local_session_id (sin manipular fechas)
        if (soloSesionActual && lastSession?.local_session_id) {
          const targetSessionId = lastSession.local_session_id;
          modifications = modifications.filter((mod: PedidoModification) => {
            const modSessionId = (mod as any).session_id ?? (mod as any).local_session_id;
            return String(modSessionId) === String(targetSessionId);
          });
        }
        setFilteredModifications(modifications)
      })
      .catch((error) => {
        // If summary call returns 404 (e.g. no modifications in range), set summary to an empty default:
          toast.error('Error cargando datos de modificaciones');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, soloSesionActual, lastSession]);


  const renderToggle = () => (
    <div className="mp-button-selector-page-container">
      <div className="mp-button-selector-page-selector">
        <button
          onClick={() => setCurrentView('table')}
          className={`mp-button-selector-page-button ${currentView === 'table' ? 'active' : 'inactive'}`}
        >
          Listado
        </button>
        <button
          onClick={() => setCurrentView('stats')}
          className={`mp-button-selector-page-button ${currentView === 'stats' ? 'active' : 'inactive'}`}
        >
          Estadísticas
        </button>
      </div>
    </div>
  );

  const titles: Record<string, { main: string; sub: string }> = {
    table: { main: 'Historial de Modificaciones', sub: 'Lista de modificaciones realizadas a los pedidos' },
    stats: { main: 'Estadísticas de Modificaciones', sub: 'Estadísticas de las modificaciones realizadas a los pedidos' },
  };
  const { main: mainTitle, sub: subTitle } = titles[currentView] || titles.table;

  // Filtros activos de fecha y otros (comportamiento igual a ReceiptsDisplay)
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [selectedMesas, setSelectedMesas] = useState<any[]>([]);

  // Apply dropdown filters (users/mesas) to the fetched modifications so desktop
  // FilterBar selections affect the displayed data just like OffCanvas does.
  const getDisplayData = () => {
    const base = Array.isArray(filteredModifications) ? filteredModifications : [];
    let filtered = base.filter(mod => {
      // Filter by users
      const userMatch = selectedUsers.length === 0 || 
        selectedUsers.some((o: any) => String(o.value) === String(mod.user_id));
      
      // Filter by mesas - compare with mesa_id from the modification
      const mesaMatch = selectedMesas.length === 0 || 
        selectedMesas.some((o: any) => String(o.value) === String(mod.mesa_id));
      
      return userMatch && mesaMatch;
    });

    // Apply search filter
    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase();
      filtered = filtered.filter((mod: any) => {
        const mesaNum = String(mod.mesa_num || '').toLowerCase();
        const userName = String(mod.user_name || '').toLowerCase();
        const detail = String(mod.modification_detail || '').toLowerCase();
        
        return mesaNum.includes(searchLower) || 
               userName.includes(searchLower) || 
               detail.includes(searchLower);
      });
    }

    return filtered;
  };

  // Handle Excel export
  const handleExportToExcel = () => {
    const displayData = getDisplayData();
    const success = exportFilteredModifications(displayData, startDate, endDate);
    
    if (success) {
      toast.success('Modificaciones exportadas exitosamente');
    } else {
      toast.error('No hay modificaciones para exportar');
    }
  };

  return (
    <div className="flex flex-col gap-4 p-3 pb-6 lg:gap-5 lg:p-4 lg:pb-8">
      <MPPageTitle subtitle={subTitle}>{mainTitle}</MPPageTitle>
      {renderToggle()}

      {/* Desktop FilterBar */}
      {!isMobile && (
        <FilterBar
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          userOptions={userOptions}
          selectedUsers={selectedUsers}
          onUsersChange={setSelectedUsers}
          mesaOptions={mesaOptions}
          selectedMesas={selectedMesas}
          onMesasChange={setSelectedMesas}
          soloSesionActual={soloSesionActual}
          setSoloSesionActual={setSoloSesionActual}
          onExportToExcel={handleExportToExcel}
          exportDisabled={loading}
          totalModifications={getDisplayData().length}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />
      )}

      {/* Mobile Filter Offcanvas */}
      {isMobile && (
        <Filter
          show={showFilters}
          setShow={setShowFilters}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          setFilteredModifications={setFilteredModifications}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          selectedMesas={selectedMesas}
          setSelectedMesas={setSelectedMesas}
          soloSesionActual={soloSesionActual}
          setSoloSesionActual={setSoloSesionActual}
        />
      )}

      {currentView === 'table' && (
        loading ? (
          <LoadingSpinner text="Cargando Modificaciones" transparent={true} />
        ) : (
          <Table data={getDisplayData()} />
        )
      )}

      {currentView === 'stats' && (
        <ModificationStats data={getDisplayData()} startDate={startDate} endDate={endDate} />
      )}
    </div>
  );
};


export default ModificacionesPedidos;
